import {
  Autocomplete,
  Box,
  Button,
  ClickAwayListener,
  Collapse,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  MenuList,
  Popper,
  Select,
  Stack,
  TablePagination,
  TextField,
  Grow,
  Divider,
  Typography
} from "@mui/material";
import * as React from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import formatter from "../../tools/formatter.js";
import moment from "moment";
import EditIcon from "@mui/icons-material/Edit";
import FileCopyIcon from '@mui/icons-material/FileCopy';
import AssessmentIcon from '@mui/icons-material/Assessment';
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import RefreshIcon from '@mui/icons-material/Refresh';
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import AddCommentIcon from '@mui/icons-material/AddComment';
import DeleteIcon from '@mui/icons-material/Delete';
import useEODAAA from "../../deal-builder/hooks/useEODAAA.ts";
import useUsTreasury from "../../deal-builder/hooks/useUsTreasury.ts";
import InputBitResultsEditor from "./deals-page/input-bit-results-editor.tsx";
import { MessageEditor } from "./deals-page/message-editor.tsx";
import { GenerateReportButton } from "./deals-page/report-button.tsx";
import { ConfirmationDialog } from "./deals-page/delete-dilog.tsx";
import { calculateSavedDealStats, DealWithStats } from "../utils/deals.utils.ts";
import usePricingManagamentAlertsStore from "../../stores/pricing-management-alerts.store.ts";
import { TeamSelector } from "./deals-page/team-selector.tsx";
import useTeams from "../hooks/useTeams.ts";
import { ArrowDropDownIcon } from "@mui/x-date-pickers";
import useDatabaseAPI, { DealBuilderSavedFormat } from "../hooks/useDatabaseAPI.ts";
import usePricingManagementStore from "../../stores/pricing-management.store.ts";
import { MainTabContext, TAB_NAMES } from "../../contexts/MainTabContext.js";
import { DealStatus } from "../../deal-builder/services/deal-builder.ts";
import useDealBuilderSaver from "../../deal-builder/hooks/useDealBuilderSaver.ts";
import useDealBuilderResultStore from "../../stores/deal-builder-result.store.ts";
import useSimilarDealsAPI from "../../deal-builder/hooks/useSimilarDealsAPI.ts";

const ALL_STATUS = [
  DealStatus.DRAFT,
  DealStatus.CLOSED,
  DealStatus.LOST,
  DealStatus.PENDING,
  DealStatus.WON,
];

export default function DealsPage() {
  const { setActiveTab } = React.useContext(MainTabContext);
  const { savedDeals, selectedTeam } = usePricingManagementStore();
  const { setDealBuilderService } = useDealBuilderResultStore();
  const { updateSimilarDeals } = useSimilarDealsAPI();
  const { loadDeals, fromSavedFormat } = useDatabaseAPI();
  const { filterDealsByTeam, } = useTeams();
  const { updateSavedDeal, saveDealAsNew, deleteSavedDeal } = useDealBuilderSaver();
  const { setSnackMessage } = usePricingManagamentAlertsStore();
  const [editingInputBidResults, setEditingInputBidResults] = React.useState<DealBuilderSavedFormat>();
  const { getAAARateForSavedDeal } = useEODAAA();
  const { getUSTRateForSavedDeal } = useUsTreasury();
  const [notesDeal, setNotesDeal] = React.useState<DealBuilderSavedFormat>();
  const [openedDeals, setOpenedDeals] = React.useState<string[]>([]);
  const [dealsList, setDealsList] = React.useState<DealBuilderSavedFormat[]>();
  const [page, setPage] = React.useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = React.useState<number>(10);
  const [visibleStatuses, setVisibleStatuses] = React.useState<DealStatus[]>(ALL_STATUS);
  const [pageDeals, setPageDeals] = React.useState<DealBuilderSavedFormat[]>([]);
  const [deletingDeal, setDeletingDeal] = React.useState<string | undefined>();

  React.useEffect(() => {
    loadDeals();
    // eslint-disable-next-line
  }, []);

  React.useEffect(() => {
    calculatePageDeals();
  }, [dealsList, page, rowsPerPage]);

  React.useEffect(() => {
    loadDealsList();
    // eslint-disable-next-line
  }, [savedDeals, visibleStatuses, selectedTeam]);

  async function onStatusSelect(deal: DealBuilderSavedFormat, newStatus: DealStatus) {
    deal.deal_status = newStatus;
    const result = await updateSavedDeal(deal);
    if (!result.valid) {
      setSnackMessage(result.error);
    }
  }

  async function onViewButton(dealId: string) {
    const savedDeal = savedDeals.find((x) => x.deal_id === dealId);
    if (!savedDeal) return;

    const deal = await fromSavedFormat(savedDeal);
    setDealBuilderService(deal);
    setActiveTab(TAB_NAMES.DEAL_BUILDER);
    updateSimilarDeals(deal, savedDeal.starred_similar_deals ?? []);
  }

  async function onDuplicateButton(dealId: string) {
    const savedDeal = savedDeals.find((x) => x.deal_id === dealId);
    if (!savedDeal) return;
    const savedFormat = await fromSavedFormat(savedDeal);
    await saveDealAsNew(savedFormat, savedDeal.deal_status);
    loadDeals();
  }

  function onDeleteConfirmationButton() {
    const savedDeal = savedDeals.find((x) => x.deal_id === deletingDeal);
    if (!savedDeal) return;
    deleteSavedDeal(savedDeal);
    setDeletingDeal(undefined);
  }

  async function onCommentChanged(deal: DealBuilderSavedFormat, comment: string) {
    deal.comment = comment;
    const result = await updateSavedDeal(deal);
    if (!result.valid) setSnackMessage(result.error);
  }

  function toggleDeal(dealId: string) {
    if (openedDeals.includes(dealId)) {
      setOpenedDeals(openedDeals.filter((x) => x !== dealId));
    } else {
      setOpenedDeals([...openedDeals, dealId]);
    }
  }

  async function loadDealsList() {
    // Filter deals to prevent unnecessary calculations by
    // removing the deals without the target status
    const dealsToList = filterDealsByTeam(savedDeals).filter((x) => {
      const filterEnabled = visibleStatuses.length !== 0;
      const includesDeal = visibleStatuses.includes(x.deal_status);

      return !filterEnabled || includesDeal;
    }).sort((a, b) => {
      const savedDateA = moment(a.saved_date).valueOf();
      const savedDateB = moment(b.saved_date).valueOf();
      return savedDateB.valueOf() - savedDateA.valueOf();
    });

    // Calculate stats and save 
    setDealsList(dealsToList);
  }

  async function calculatePageDeals() {
    const targetDeals = [...dealsList || []].slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
    setPageDeals(targetDeals);
  }

  return (
    <Stack direction="column" gap={2} margin={2} sx={{ width: "100%" }}>
      <Paper>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          m={1}
        >
          <Autocomplete
            multiple
            value={visibleStatuses}
            options={ALL_STATUS}
            renderInput={(params) => (
              <TextField
                {...params}
                sx={{ minWidth: "200px" }}
                size="small"
                label="Filter by status"
              />
            )}
            onChange={(e, newValue) => setVisibleStatuses(newValue)}
          />

          <Stack direction="row" gap={2}>
            <Button
              onClick={() => loadDeals()}
              variant="outlined"
              startIcon={<RefreshIcon />}
            >
              Refresh List
            </Button>
            <GenerateReportButton />
          </Stack>
        </Stack>
      </Paper>
      
      <TeamSelector />

      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell align="center"></TableCell>
                <TableCell align="left">
                  <b>Report Titles</b>
                </TableCell>
                <TableCell align="left">
                  <b>Priced as of</b>
                </TableCell>
                <TableCell sx={{ py: "16px" }} align="left">
                  <b>Bid Date</b>
                </TableCell>
                <TableCell align="left">
                  <b>Closing Date</b>
                </TableCell>
                <TableCell align="right">
                  <b>Par Amount</b>
                </TableCell>
                <TableCell align="left">
                  <b>Status</b>
                </TableCell>
                <TableCell align="left">
                  <b>Actions</b>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!dealsList ? (
                <TableRow>
                  <TableCell colSpan={99} sx={{ py: "12px" }} align="center">
                    Loading...
                  </TableCell>
                </TableRow>
              ) : pageDeals.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={99} sx={{ py: "12px" }} align="center">
                    No results.
                  </TableCell>
                </TableRow>
              ) : (
                pageDeals.map((deal, index) => {
                  const isOpen = openedDeals.includes(deal.deal_id);

                  return (
                    <React.Fragment key={index}>
                      <TableRow>
                        <TableCell align="center">
                          <IconButton
                            size="small"
                            onClick={() => toggleDeal(deal.deal_id)}
                          >
                            {isOpen ? (
                              <KeyboardArrowDownIcon color="primary" />
                            ) : (
                              <KeyboardArrowUpIcon color="primary" />
                            )}
                          </IconButton>
                        </TableCell>
                        <TableCell
                          align="left"
                          sx={{ verticalAlign: "center" }}
                        >
                          {deal.borrower || "-"}<br />
                          {deal.issuance_title || "-"}
                        </TableCell>
                        <TableCell
                          sx={{ verticalAlign: "center" }}
                          align="left"
                          component="th"
                          scope="row"
                        >
                          {moment(deal.market_conditions_date).format("MM/DD/YYYY")}
                        </TableCell>
                        <TableCell
                          sx={{ verticalAlign: "center" }}
                          align="left"
                          component="th"
                          scope="row"
                        >
                          {moment(deal.bid_date).format("MM/DD/YYYY")}
                        </TableCell>
                        <TableCell
                          align="left"
                          sx={{ verticalAlign: "center" }}
                        >
                          {moment(deal.delivery_date).format("MM/DD/YYYY")}
                        </TableCell>
                        <TableCell align="right" sx={{ verticalAlign: "center" }}>
                          {formatter.money(deal.par_amount)}
                        </TableCell>

                        <TableCell 
                          align="left"
                          width="130px"
                        >
                          <FormControl>
                            <InputLabel></InputLabel>
                            <Select
                              label=""
                              size="small"
                              value={deal.deal_status}
                              sx={{ minWidth: "140px" }}
                              onChange={(e) =>
                                onStatusSelect(deal, e.target.value as DealStatus)
                              }
                            >
                              <MenuItem value={DealStatus.DRAFT}>
                                Draft
                              </MenuItem>
                              <MenuItem value={DealStatus.PENDING}>
                                Pending Bid
                              </MenuItem>
                              <MenuItem value={DealStatus.WON}>Won</MenuItem>
                              <MenuItem value={DealStatus.CLOSED}>
                                Closed
                              </MenuItem>
                              <MenuItem value={DealStatus.LOST}>Lost</MenuItem>
                            </Select>
                          </FormControl>
                        </TableCell>
                        <TableCell 
                          align="left" 
                          width="130px"
                          sx={{ py: 0.75 }}
                        >
                          <ActionsButton deal={deal} />
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell colSpan={1} />
                        <TableCell
                          style={{ paddingBottom: 0, paddingTop: 0 }}
                          colSpan={99}
                        >
                          <Collapse in={isOpen} timeout="auto" unmountOnExit>
                            <CollapsableDealData deal={deal} />
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  );
                })
              )}
            </TableBody>
          </Table>

          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={dealsList ? dealsList.length : 0}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(_e, val) => setPage(val)}
            onRowsPerPageChange={(e) => setRowsPerPage(parseInt(e.target.value))}
          />
        </TableContainer>
      </Paper>

      {editingInputBidResults && (
        <InputBitResultsEditor
          deal={editingInputBidResults}
          onClose={() => setEditingInputBidResults(undefined)}
          onSave={(deal) => {
            updateSavedDeal(deal);
            setEditingInputBidResults(undefined);
          }}
        />
      )}

      {notesDeal && (
        <MessageEditor
          message={notesDeal.comment}
          onClose={() => setNotesDeal(undefined)}
          onSave={(msg) => {
            onCommentChanged(notesDeal, msg);
            setNotesDeal(undefined);
          }}
        />
      )}

      <ConfirmationDialog
        title="Are you should you want to delete this deal?"
        description="This decision can not be undone."
        onAccept={onDeleteConfirmationButton}
        onClose={() => setDeletingDeal(undefined)}
        open={!!deletingDeal}
      />
    </Stack>
  );

  interface ActionsButtonProps { deal: DealBuilderSavedFormat }
  function ActionsButton({ deal }: ActionsButtonProps) {
    const [openActions, setOpenActions] = React.useState(false);
    const actionsAnchorRef = React.useRef<HTMLButtonElement>(null);
    
    return (
      <>
        <Button
          ref={actionsAnchorRef}
          onClick={() => setOpenActions(true)}
          variant="contained"
          endIcon={<ArrowDropDownIcon />}
          sx={{ color: "white" }}
        >
          Actions
        </Button>
        <Popper
          open={openActions}
          anchorEl={actionsAnchorRef.current}
          role={undefined}
          placement="bottom-start"
          transition
          disablePortal
          sx={{ zIndex: 999 }}
        >
          {({ TransitionProps, placement }) => (
            <Grow
              {...TransitionProps}
              style={{
                transformOrigin:
                  placement === 'bottom-start' ? 'left top' : 'left bottom',
              }}
            >
              <Paper>
                <ClickAwayListener onClickAway={() => setOpenActions(false)}>
                  <MenuList autoFocusItem={openActions}>
                    <MenuItem disableRipple onClick={() => onViewButton(deal.deal_id)}>
                      <EditIcon color="primary" fontSize="small" />
                      <Box ml={1}>View / Edit</Box>
                    </MenuItem>
                    <MenuItem disableRipple onClick={() => onDuplicateButton(deal.deal_id)}>
                      <FileCopyIcon color="primary" fontSize="small" />
                      <Box ml={1}>Duplicate</Box>
                    </MenuItem>
                    <Divider sx={{ my: 0.5 }} />
                    <MenuItem disableRipple onClick={() => setEditingInputBidResults(deal)}>
                      <AssessmentIcon color="primary" fontSize="small" />
                      <Box ml={1}>Input Bid Results</Box>
                    </MenuItem>
                    <MenuItem disableRipple onClick={() => setNotesDeal(deal)}>
                      <AddCommentIcon  color="primary" fontSize="small" />
                      <Box ml={1}>Add Comment</Box>
                    </MenuItem>
                    <Divider sx={{ my: 0.5 }} />
                    <MenuItem disableRipple onClick={() => setDeletingDeal(deal.deal_id)}>
                      <DeleteIcon color="error" fontSize="small" />
                      <Box ml={1} color="red">Delete deal</Box>
                    </MenuItem>
                  </MenuList>
                </ClickAwayListener>
              </Paper>
            </Grow>
          )}
        </Popper>
      </>
    )
  }

  interface CollapsableDealDataProps { deal: DealBuilderSavedFormat }
  function CollapsableDealData({ deal }: CollapsableDealDataProps) {
    const [stats, setStats] = React.useState<DealWithStats>();

    React.useEffect(() => {
      async function run() {
        setStats(await calculateSavedDealStats(
          deal,
          getAAARateForSavedDeal,
          getUSTRateForSavedDeal,
        ))
      }
      run();
    }, []);
    
    const ticStr = deal.tic ? formatter.percent(deal.tic, 2) : "-";
    const ticExpensesStr = deal.tic_expenses ? formatter.percent(deal.tic_expenses, 2) : "-";

    const varianceStr = deal.variance ? formatter.money(deal.variance) : "-";
    const varianceExpensesStr = deal.variance_expenses ? formatter.money(deal.variance_expenses) : "-";
  
    const coverBid = deal.other_bids.length === 0 ? undefined : Math.min(...deal.other_bids.map(x => x[1]));
    const coverBidSpread = coverBid ? coverBid - deal.bid_rate : undefined;
    const coverBidSpreadStr = coverBidSpread ? formatter.percent(coverBidSpread, 2) : "N/A";

    return (
      <Stack direction="column" gap={2} margin={2}>
        <Box>
          <Typography variant="subtitle2" gutterBottom component="div">
            Advisor
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {deal.advisor || "Not available"}
          </Typography>
        </Box>
        <Box>
          <Typography variant="subtitle2" gutterBottom component="div">
            Deal Details
          </Typography>
          <Typography 
            variant="body2" 
            color="text.secondary"
            sx={{
              "& > div": { width: "90px", display: "inline-block" }
            }}
          >
            <Box>Security:</Box> {deal.security_type} <br />
            <Box>State:</Box> {deal.state_of_issuance} <br />
            <Box>Tax:</Box> {deal.tax_designation} <br />
            <Box>Rating:</Box> {deal.rating_assumption} <br />
            <Box>Average life:</Box> {deal.average_life ? deal.average_life.toFixed(2) + "Y" : "N/A"}<br />
          </Typography>
        </Box>
        <Box>
          <Typography variant="subtitle2" gutterBottom component="div">
            Rates
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Bid rate: {formatter.percent(deal.bid_rate)}<br />
            Cover bid: {coverBidSpreadStr}<br />
            Tic: {ticStr} / {ticExpensesStr}<br />
            Var: {varianceStr} / {varianceExpensesStr}<br />
          </Typography>
        </Box>
        {deal.comment.length !== 0 && (
          <Box>
            <Typography variant="subtitle2" gutterBottom component="div">
              Comment
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {deal.comment}
            </Typography>
          </Box>
        )}

        {stats && (
          
          <Table size="small" sx={{ maxWidth: "800px" }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ border: "none", px: 0 }}>
                  <Typography variant="subtitle2" gutterBottom component="div">
                    Bidder
                  </Typography>
                </TableCell>
                <TableCell sx={{ border: "none", px: 0 }}>
                  <Typography variant="subtitle2" gutterBottom component="div">
                    Bid Rate
                  </Typography>
                </TableCell>
                <TableCell sx={{ border: "none", px: 0 }}>
                  <Typography variant="subtitle2" gutterBottom component="div">
                    Tic Spread
                  </Typography>
                </TableCell>
                <TableCell sx={{ border: "none", px: 0 }}>
                  <Typography variant="subtitle2" gutterBottom component="div">
                    AAA Spread
                  </Typography>
                </TableCell>
                <TableCell sx={{ border: "none", px: 0 }}>
                  <Typography variant="subtitle2" gutterBottom component="div">
                    UST Spread
                  </Typography>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell sx={{ border: "none", px: 0 }}>
                  <Typography variant="body2" color="text.secondary">
                    Your BID
                  </Typography>
                </TableCell>
                <TableCell sx={{ border: "none", px: 0 }}>
                  <Typography variant="body2" color="text.secondary">
                    {formatter.percent(deal.bid_rate)}
                  </Typography>
                </TableCell>
                <TableCell sx={{ border: "none", px: 0 }}>
                  <Typography variant="body2" color="text.secondary">
                    {formatter.percent(stats.ticSpread)}
                  </Typography>
                </TableCell>
                <TableCell sx={{ border: "none", px: 0 }}>
                  <Typography variant="body2" color="text.secondary">
                    {stats.aaaSpread ? formatter.percent(stats.aaaSpread) : "-"}
                  </Typography>
                </TableCell>
                <TableCell sx={{ border: "none", px: 0 }}>
                  <Typography variant="body2" color="text.secondary">
                    {stats.ustSpread ? formatter.percent(stats.ustSpread) : "-"}
                  </Typography>
                </TableCell>
              </TableRow>

              {[ ...deal.other_bids ].sort((a, b) => a[1] - b[1]).map((bid, index) => {
                return (
                  <TableRow key={index} sx={{ border: "none", px: 0 }}>
                    <TableCell sx={{ border: "none", px: 0 }}>
                      <Typography variant="body2" color="text.secondary">
                        {bid[0]}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ border: "none", px: 0 }}>
                      <Typography variant="body2" color="text.secondary">
                        {formatter.percent(bid[1])}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ border: "none", px: 0 }}>
                      <Typography variant="body2" color="text.secondary">
                        {deal.tic ? formatter.percent(bid[1] - deal.tic) : "N/A"}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ border: "none", px: 0 }}>
                      <Typography variant="body2" color="text.secondary">
                        {stats.aaaRate ? formatter.percent(bid[1] - stats.aaaRate) : "-"}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ border: "none", px: 0 }}>
                      <Typography variant="body2" color="text.secondary">
                        {stats.ustRate ? formatter.percent(bid[1] - stats.ustRate) : "-"}
                      </Typography>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
        
      </Stack>
    )
  }
}
